import React, { useState, useEffect, useContext, useRef } from 'react';
import 'twin.macro';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Input } from '../../form';
import { Icon } from '../../icon';
import { Title, Paragraph, Small } from '../../text';
import {
  ArrowIcon,
  EditIcon,
  CheckIcon,
  TimesIcon,
  DumpertSingleDLogoIcon,
  VideoIcon,
  ImageIcon,
  PrivateIcon,
  ListPlayIcon,
  ArrowFullIcon,
  NsfwIcon,
} from 'icons';
import { formatNumber, formatDate, formatDuration, pluralize } from '../../../lib/helpers';
import { PlaylistAutoSuggestSearch, PlaylistItemDragAndDrop } from '.';
import { Button } from '../../button';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../../table';
import { DropDown, DropDownItem } from '../../dropdown';
import { LoadingDots } from '../../loading';
import { SocialSharing } from '../../social';
import { Kudos, KudoAmount } from '../../kudos';
import { PlaylistContext } from '../../context';
import { Divider } from '../../divider';
import { ItemImage } from '../../item';
import { PLAYLIST_PATH } from '../../../data/';

export const Playlist = () => {
  const {
    userCanEdit,
    currentUserId,
    playListData,
    playlistItems,
    loading,
    isPrivateList,
    totalPlaylistDuration,
    handleEditPlaylist,
    editTitleActive,
    setEditTitleActive,
    editDescActive,
    setEditDescActive,
    name,
    setName,
    desc,
    setDesc,
    playlistKudoAction,
  } = useContext(PlaylistContext);

  const location = useLocation();
  const navigate = useNavigate();

  const titleInputRef = useRef();
  const descInputRef = useRef();

  const {
    id: playlistId,
    title,
    description,
    kudos,
    follows,
    uploader_username: uploaderUsername,
    private: isPrivate,
    created_at: createdAt,
  } = playListData || {};

  useEffect(() => {
    if (location.search === '?edit=true') {
      setEditTitleActive(!editTitleActive);
    }
  }, [location]);

  useEffect(() => {
    if (editTitleActive) {
      setTimeout(() => titleInputRef.current.focus(), 250);
    }
  }, [editTitleActive]);

  useEffect(() => {
    if (editDescActive) {
      descInputRef.current.focus();
    }
  }, [editDescActive]);

  if (isPrivateList) {
    return (
      <>
        <PlaylistBackButton />
        <div tw='p-8 mb-8 rounded-lg border-b border-secondary'>
          <div tw='flex gap-4 justify-center items-center h-[200px] mb-8'>
            <PrivateIcon />
          </div>
          <div tw='text-center'>
            <Title tw='text-center w-full mb-4'>Deze afspeellijst is Prive</Title>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PlaylistBackButton />
      {loading && (
        <div tw='w-full col-span-3'>
          <LoadingDots tw='mx-auto' />
        </div>
      )}
      {!loading && playListData && (
        <>
          <div tw='md:flex justify-start items-start mb-4 gap-4'>
            <div tw='mx-auto mb-4 w-32 bg-secondary rounded-lg aspect-[1/1]'>
              {!(playlistItems && playlistItems.length > 0) ||
              playlistItems[0].stills['still-medium'] === '' ? (
                <div tw='h-full w-full bg-brand flex justify-center items-center rounded-lg'>
                  <DumpertSingleDLogoIcon tw='mx-auto fill-light h-10 w-10' />
                </div>
              ) : (
                <ItemImage
                  item={playlistItems[0]}
                  isTopper={false}
                  landscape={false}
                  isPlaylistItem
                  hideSummary
                  tw='w-full h-full'
                />
              )}
            </div>
            <div tw='w-full'>
              <div tw='flex justify-start items-start gap-2'>
                {userCanEdit && (
                  <Icon
                    Type={!editTitleActive ? EditIcon : TimesIcon}
                    tw='float-left cursor-pointer'
                    onClick={() => setEditTitleActive(!editTitleActive)}
                  />
                )}

                {!editTitleActive && (
                  <Title
                    marginBottom={false}
                    tw='flex-grow'
                    onClick={(e) => {
                      if (e.detail === 2) setEditTitleActive(!editTitleActive);
                    }}
                  >
                    {title || <i>{'[Geen titel toegevoegd]'}</i>}
                  </Title>
                )}
                {userCanEdit && (
                  <>
                    {editTitleActive && (
                      <form
                        tw='flex flex-grow justify-start items-center gap-2'
                        onSubmit={() => {
                          handleEditPlaylist(isPrivate);
                          setEditTitleActive(!editTitleActive);
                          if (location.search === '?edit=true') {
                            navigate(location.pathname);
                          }
                        }}
                      >
                        <Input
                          ref={titleInputRef}
                          placeholder='Titel'
                          name='playlist-title'
                          id='playlist-title'
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          requiredField
                          tw='mb-0'
                        />
                        <Button
                          label='opslaan'
                          primary
                          type='submit'
                          iconProps={{
                            Type: CheckIcon,
                            color: 'dark',
                            size: 'medium',
                          }}
                        />
                      </form>
                    )}
                    <DropDown label={isPrivate ? 'Privé' : 'Openbaar'} isSelect isRight>
                      <DropDownItem
                        label='Privé'
                        selectedValue={isPrivate}
                        onClick={!isPrivate ? () => handleEditPlaylist(!isPrivate) : null}
                      />
                      <DropDownItem
                        label='Openbaar'
                        selectedValue={!isPrivate}
                        onClick={isPrivate ? () => handleEditPlaylist(!isPrivate) : null}
                      />
                    </DropDown>
                  </>
                )}
              </div>
              <div tw='flex w-full justify-start items-center gap-2'>
                {userCanEdit && (
                  <Icon
                    Type={!editDescActive ? EditIcon : TimesIcon}
                    tw='cursor-pointer shrink-0'
                    onClick={() => setEditDescActive(!editDescActive)}
                  />
                )}
                {!editDescActive && (
                  <Paragraph
                    onClick={(e) => {
                      if (e.detail === 2) setEditDescActive(!editDescActive);
                    }}
                  >
                    {description || <i>{'[Geen omschrijving toegevoegd]'}</i>}
                  </Paragraph>
                )}
                {userCanEdit && editDescActive && (
                  <form
                    tw='flex flex-grow justify-start items-center gap-2'
                    onSubmit={() => {
                      handleEditPlaylist(isPrivate);
                      setEditDescActive(!editDescActive);
                    }}
                  >
                    <div tw='flex-grow'>
                      <Input
                        ref={descInputRef}
                        placeholder='Omschrijving'
                        name='playlist-description'
                        id='playlist-description'
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        requiredField
                        tw='mb-0 '
                      />
                    </div>
                    <Button
                      label='opslaan'
                      primary
                      type='submit'
                      iconProps={{
                        Type: CheckIcon,
                        color: 'dark',
                        size: 'medium',
                      }}
                    />
                  </form>
                )}
              </div>
            </div>
            {!isPrivate && playlistItems.length > 0 && (
              <div tw='pt-2 md:p-0 flex-shrink'>
                <SocialSharing
                  title='deze afspeellijst'
                  path={`${PLAYLIST_PATH.public}/${currentUserId}/${playlistId}`}
                  tw='justify-center'
                  isRight
                />
              </div>
            )}
          </div>
          <div tw='flex justify-between gap-4 mb-6'>
            <div tw='flex flex-col'>
              {follows > 0 && <Small>Volgers: {follows}</Small>}
              {uploaderUsername !== '' && (
                <Small>
                  <strong>Eigenaar:</strong> {uploaderUsername}
                </Small>
              )}
              {createdAt !== '' && createdAt !== '0001-01-01T00:00:00Z' && (
                <Small>
                  <strong>Datum:</strong> {formatDate(createdAt)}
                </Small>
              )}
            </div>
            <Kudos
              numOfKudos={typeof kudos === 'number' && kudos > 0 ? kudos : Number(0)}
              large
              hideActions={userCanEdit}
              clickUp={() => {
                // gaEvent('dmp_detail_page', 'kudo_up');
                return playlistKudoAction({ playlistId, direction: 'up' });
              }}
              clickDown={() => {
                // gaEvent('dmp_detail_page', 'kudo_down');
                return playlistKudoAction({ playlistId, direction: 'down' });
              }}
            />
          </div>

          {playlistItems && playlistItems.length > 0 && (
            <>
              <Divider />
              <div tw='flex flex-col md:flex-row gap-2 items-center'>
                <Link to={`${PLAYLIST_PATH.public}/${currentUserId}/${playlistId}`}>
                  <Button
                    label='Alles afspelen'
                    iconProps={{ Type: ListPlayIcon, color: 'dark' }}
                    primary
                  />
                </Link>
                <Small tw='flex flex-grow text-center'>
                  {`
            Aantal: ${pluralize(playlistItems.length, 'item')} | 
            totale lengte: ${totalPlaylistDuration}
          `}
                </Small>
                {playListData.playlist_nsfw && <Icon Type={NsfwIcon} color='light' />}
              </div>
            </>
          )}
          {userCanEdit && playlistItems && (
            <>
              <Divider />
              <PlaylistAutoSuggestSearch />
            </>
          )}
          {playlistItems && playlistItems.length === 0 && <>Geen items</>}
          {playlistItems && playlistItems.length > 0 && (
            <>
              <div tw='w-full overflow-x-scroll text-sm'>
                {!userCanEdit && (
                  <div tw='min-w-[500px]'>
                    <Table tw='min-w-full'>
                      <TableHead>
                        <TableRow>
                          <TableCell head></TableCell>
                          <TableCell head tw='max-w-[250px] md:max-w-full'>
                            Titel
                          </TableCell>
                          <TableCell head>Kudos</TableCell>
                          <TableCell head>Media</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <PlaylistRows list={playlistItems} playlistId={playlistId} />
                      </TableBody>
                    </Table>
                  </div>
                )}
                {userCanEdit && <PlaylistItemDragAndDrop playlistId={playlistId} />}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

const PlaylistRows = ({ list, playlistId }) => {
  const [items, setItems] = useState(list);

  useEffect(() => {
    setItems(list);
  }, [list]);

  return (
    <>
      {items.sort().map((item, index) => {
        return <PlaylistRow key={index} item={item} playlistId={playlistId} />;
      })}
    </>
  );
};

export const PlaylistRow = ({ item, playlistId }) => {
  const { currentUserId } = useContext(PlaylistContext);

  const {
    title,
    date,
    composite_id: compositeId,
    media_type: mediaType,
    stats: { kudos_total: kudosTotal, views_total: viewsTotal },
    media,
  } = item;

  return (
    <TableRow>
      <TableCell>
        <Link to={`${PLAYLIST_PATH.public}/${currentUserId}/${playlistId}/${compositeId}`}>
          <Button
            iconProps={{ Type: ArrowFullIcon, size: 'small', color: 'dark' }}
            tw='-rotate-90 w-7 h-7 mr-0'
            primary
          />
        </Link>
      </TableCell>
      <TableCell tw='max-w-[250px] md:max-w-full'>
        <Link to={`?selectedId=${compositeId}`}>
          <div tw='flex items-center'>
            <div tw='h-14 w-14 flex-shrink-0'>
              <ItemImage item={item} isPlaylistItem hideSummary />
            </div>
            <div tw='ml-4'>
              <div tw='font-bold line-clamp-1'>{title}</div>
              <Small>
                {formatDate(date)} | Views: {formatNumber(viewsTotal)}
              </Small>
            </div>
          </div>
        </Link>
      </TableCell>
      <TableCell>
        <KudoAmount numKudos={kudosTotal} hideActions leftAlign />
      </TableCell>
      <TableCell>
        <div tw='flex gap-2 items-center'>
          <Icon Type={mediaType === 'VIDEO' ? VideoIcon : ImageIcon} size='small' color='icons' />
          {formatDuration(media[0].duration, 1, 'VIDEO')}
        </div>
      </TableCell>
    </TableRow>
  );
};

const PlaylistBackButton = () => {
  const { currentUserId } = useContext(PlaylistContext);

  return (
    <>
      <div tw='flex justify-between mb-6'>
        {currentUserId && (
          <Link
            to={`${PLAYLIST_PATH.userPlaylists}/${currentUserId}`}
            tw='flex mb-4 items-center text-icons'
          >
            <Icon Type={ArrowIcon} size='medium' tw='float-left -rotate-90' /> terug naar
            afspeellijsten
          </Link>
        )}
      </div>
    </>
  );
};
